import { type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import {
  DenseTable,
  defaultNumberFormat,
  estimateSuffix,
  type ChartData,
  type ChartSeries,
  type Column,
} from "../../components/index.ts";
import { ChartCard } from "../../components/observability/index.ts";
import type { components } from "@oathera/contract/api";
import type { TokenStats } from "./use-identity.ts";
import { denialReasonLabel, quantityMeasure, tokenDenialHref } from "./vocab.ts";

/**
 * The tokens region (task 11.7; design 12.2 Identity "token issuance and denial reasons"; 12.3
 * drill-down, "sampled labelled, estimates 'est.'"; 12.4 chart via the Chart wrapper, semantic
 * table).
 *
 * Two views from the one `TokenStats` snapshot:
 *
 * - ISSUANCE OVER TIME: the contract `series` of issued/denied per bucket, now presented inside the
 *   observability kit's `ChartCard` (observability-ui task 4.1), which wraps the same shared `Chart`
 *   wrapper so it keeps the always-present data-table alternative and the synced crosshair, and adds
 *   the kit's titled panel chrome + live/paused indicator so this region reads like the Overview and
 *   the other section rollouts. This is PRESENTATION-ONLY — the plotted data is the identical
 *   contract `series`; nothing in the data layer changed. The series values are plain counts (the
 *   contract types them as `number`, not `Quantity`), so no per-series estimate flag is claimed here
 *   — the row counts below carry the exact/estimate confidence instead.
 * - DENIAL REASONS: the denied `rows` aggregated by `denial_reason` (summing the `Quantity` counts
 *   across kinds and versions), busiest first, each drilling to the token-denial events behind it.
 *   A reason's confidence (exact vs sampled est.) is shown on its count. Issued rows are summarised
 *   separately so the denial breakdown stays focused on denials (design 12.2 "denial reasons").
 */

export interface TokenPanelProps {
  readonly data: TokenStats;
}

type TokenRow = TokenStats["rows"][number];
type DenialReason = components["schemas"]["enum_denial_reason"];
type Measure = ReturnType<typeof quantityMeasure>;

/** An aggregated denial-reason row: total count across kinds/versions, and the strongest measure. */
interface DenialAggregate {
  readonly reason: DenialReason;
  readonly count: number;
  /** `unverifiable` if any contributing count was null; else `estimate` if any was sampled. */
  readonly measure: Measure;
}

export function TokenPanel(props: TokenPanelProps): ReactNode {
  const navigate = useNavigate();
  const { data } = props;

  const denials = aggregateDenials(data.rows);
  const issuedTotal = sumIssued(data.rows);

  const points = data.series;
  const xs = points.map((p) => p.t);
  const issued = points.map((p) => p.issued);
  const denied = points.map((p) => p.denied);
  const chartData: ChartData = [xs, issued, denied];
  const chartSeries: readonly ChartSeries[] = [
    { label: "Issued", colorVar: "--status-healthy" },
    { label: "Denied", colorVar: "--status-error" },
  ];

  const denialColumns: readonly Column<DenialAggregate>[] = [
    {
      key: "reason",
      header: "Denial reason",
      isRowHeader: true,
      cell: (r) => (
        <button
          type="button"
          className="idn-link"
          onClick={() => navigate(tokenDenialHref(r.reason))}
          aria-label={`Open the token-denial events for ${denialReasonLabel(r.reason).toLowerCase()}`}
        >
          {denialReasonLabel(r.reason)}
        </button>
      ),
    },
    {
      key: "count",
      header: "Denied",
      align: "right",
      cell: (r) => (
        <span className="idn-num" data-measure={r.measure}>
          {defaultNumberFormat(r.count)}
          {estimateSuffix(r.measure)}
        </span>
      ),
    },
  ];

  return (
    <section className="idn-tokens" aria-label="Token issuance and denials">
      {/* Issuance over time, folded into the kit's ChartCard. ChartCard owns the title + live/paused
          indicator (read from the shared time-range); the window total moves into its footer.
          ChartCard's x/xIsMillis default to time/ms, exactly what this contract series uses. */}
      <ChartCard
        title="Token issuance and denials over time"
        data={chartData}
        series={chartSeries}
        height={220}
        empty="No tokens were issued or denied in this window. Widen the time range, or check that the AIS is reporting token outcomes."
        footer={
          <span className="idn-section__note">
            {defaultNumberFormat(issuedTotal)} issued in window
          </span>
        }
      />

      <div className="idn-denials">
        <h3 className="idn-subhead">Denial reasons</h3>
        <DenseTable
          caption="Token denials by reason"
          captionHidden
          columns={denialColumns}
          rows={denials}
          rowKey={(r) => r.reason}
          emptyMessage="No token denials in this window."
        />
      </div>
    </section>
  );
}

/**
 * Aggregate the denied rows by `denial_reason`, summing the `Quantity` counts across token kinds and
 * versions. A null count contributes nothing to the sum but marks the aggregate `unverifiable`; a
 * sampled (estimated) count marks it `estimate`; otherwise it stays `exact`. Rows with outcome
 * `issued` (or denial_reason `none`) are not denials and are excluded. Busiest first.
 */
function aggregateDenials(rows: readonly TokenRow[]): DenialAggregate[] {
  const byReason = new Map<DenialReason, { count: number; hasNull: boolean; hasEst: boolean }>();
  for (const row of rows) {
    if (row.outcome !== "denied") continue;
    if (row.denial_reason === "none") continue;
    const acc = byReason.get(row.denial_reason) ?? { count: 0, hasNull: false, hasEst: false };
    if (row.count.value === null) {
      acc.hasNull = true;
    } else {
      acc.count += row.count.value;
      if (row.count.exactness === "estimated") acc.hasEst = true;
    }
    byReason.set(row.denial_reason, acc);
  }

  const aggregates: DenialAggregate[] = [];
  for (const [reason, acc] of byReason) {
    const measure: Measure = acc.hasNull ? "unverifiable" : acc.hasEst ? "estimate" : "exact";
    aggregates.push({ reason, count: acc.count, measure });
  }
  // Busiest first; equal counts fall back to the label for a stable order.
  aggregates.sort((a, b) => b.count - a.count || denialReasonLabel(a.reason).localeCompare(denialReasonLabel(b.reason)));
  return aggregates;
}

/** Total issued count across all issued rows (null counts contribute nothing). */
function sumIssued(rows: readonly TokenRow[]): number {
  let total = 0;
  for (const row of rows) {
    if (row.outcome === "issued" && row.count.value !== null) total += row.count.value;
  }
  return total;
}
